/**
 * Week Start Section
 * Lets an admin choose the first day of the week (Monday or Sunday) used for
 * the Scheduled Dashboard "Current Week" and weekly OTIF windows. Backed by
 * PlatformSettings.week_start_day (0=Monday, 6=Sunday).
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { CalendarDays } from 'lucide-react';
import { platformSettingsAPI } from '../../../lib/api';
import { toast } from 'sonner';

const WEEK_START_OPTIONS = [
  { value: 0, label: 'Monday (Mon–Sun)' },
  { value: 6, label: 'Sunday (Sun–Sat)' },
];

export default function WeekStartSection() {
  const [weekStartDay, setWeekStartDay] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    platformSettingsAPI
      .getSettings()
      .then((resp) => {
        if (active && resp.data) {
          setWeekStartDay(resp.data.week_start_day ?? 0);
        }
      })
      .catch((err: unknown) => {
        console.error('Error loading week start setting:', err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleChange = async (value: number) => {
    const previous = weekStartDay;
    setWeekStartDay(value);
    try {
      await platformSettingsAPI.patchSettings({ week_start_day: value });
      toast.success('Week start day saved');
    } catch (err: unknown) {
      console.error('Error saving week start day:', err);
      toast.error('Failed to save week start day');
      setWeekStartDay(previous);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-semibold uppercase tracking-wide">
          <CalendarDays className="h-4 w-4" />
          Weekly OTIF Week Start
        </CardTitle>
        <CardDescription>
          First day of the week used for the Scheduled Dashboard "Current Week"
          tab and weekly OTIF measurement windows.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <Label htmlFor="week-start-day" className="text-sm text-muted-foreground">
          Week starts on
        </Label>
        <select
          id="week-start-day"
          value={weekStartDay}
          disabled={loading}
          onChange={(e) => handleChange(parseInt(e.target.value, 10))}
          className="flex h-10 w-[220px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {WEEK_START_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </CardContent>
    </Card>
  );
}
